import { useRef, useState } from 'react';
import type { User } from '@/hooks/useAuth';
import { updateMyProfile, uploadAvatar } from '@/services/userService';

interface ProfileHeaderProps {
  user: User;
  bio: string | null;
  onUpdated: () => void;
  onNotify: (msg: string) => void;
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export default function ProfileHeader({
  user,
  bio,
  onUpdated,
  onNotify,
}: ProfileHeaderProps) {
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(user.name);
  const [bioDraft, setBioDraft] = useState(bio ?? '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const startEdit = () => {
    setDisplayName(user.name);
    setBioDraft(bio ?? '');
    setEditing(true);
  };

  const save = async () => {
    const name = displayName.trim();
    if (!name) {
      onNotify('Ton pseudo ne peut pas être vide.');
      return;
    }
    setSaving(true);
    try {
      await updateMyProfile({ displayName: name, bio: bioDraft.trim() || null });
      setEditing(false);
      onUpdated();
      onNotify('Profil mis à jour !');
    } catch (err) {
      onNotify(err instanceof Error ? err.message : 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      await uploadAvatar(file);
      onUpdated();
      onNotify('Photo de profil mise à jour !');
    } catch (err) {
      onNotify(err instanceof Error ? err.message : 'Erreur lors du téléversement');
    } finally {
      setUploading(false);
    }
  };

  const roles: string[] = [];
  if (user.isAdmin) roles.push('Administrateur');
  if (user.isModerator) roles.push('Modérateur');

  return (
    <div className="bg-white border border-background-200 rounded-lg p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        {/* Avatar */}
        <div className="relative">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="relative block cursor-pointer"
            aria-label="Changer la photo de profil"
          >
            {user.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={`Avatar de ${user.name}`}
                className="w-20 h-20 rounded-full object-cover border-2 border-background-200"
              />
            ) : (
              <span className="w-20 h-20 rounded-full bg-primary-500 text-white flex items-center justify-center text-2xl font-bold">
                {getInitials(user.name)}
              </span>
            )}
            <span className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-white border border-background-200 text-foreground-600 flex items-center justify-center">
              {uploading ? (
                <i className="ri-loader-4-line text-sm animate-spin"></i>
              ) : (
                <i className="ri-camera-line text-sm"></i>
              )}
            </span>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarPick}
            className="hidden"
          />
        </div>

        {/* Infos */}
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex flex-col gap-3">
              <div>
                <label htmlFor="profile-name" className="block text-xs font-medium text-foreground-500 mb-1">
                  Pseudo
                </label>
                <input
                  id="profile-name"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  maxLength={50}
                  className="w-full px-3 py-2 rounded-md border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div>
                <label htmlFor="profile-bio" className="block text-xs font-medium text-foreground-500 mb-1">
                  Bio
                </label>
                <textarea
                  id="profile-bio"
                  value={bioDraft}
                  onChange={(e) => setBioDraft(e.target.value)}
                  rows={3}
                  maxLength={300}
                  placeholder="Parle un peu de toi, de tes envies, de ton quartier…"
                  className="w-full px-3 py-2 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
                />
                <p className="mt-1 text-right text-xs text-foreground-400">{bioDraft.length}/300</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 flex items-center gap-2"
                >
                  {saving && <i className="ri-loader-4-line animate-spin"></i>}
                  Enregistrer
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(false)}
                  className="px-4 py-2 rounded-md border border-background-300 text-sm font-semibold text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                  {user.name}
                </h1>
                {roles.map((r) => (
                  <span
                    key={r}
                    className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold"
                  >
                    {r}
                  </span>
                ))}
              </div>
              <p className="mt-0.5 text-sm text-foreground-500">{user.email}</p>
              <p className="mt-3 text-sm text-foreground-700 leading-relaxed max-w-xl">
                {bio || 'Aucune bio pour l’instant. Clique sur « Modifier » pour te présenter.'}
              </p>
              <button
                type="button"
                onClick={startEdit}
                className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-background-300 text-sm font-medium text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-pencil-line text-base"></i>
                Modifier
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}